import { StyleType, CssColor, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import {
  StyleParamType,
  wixColorParam,
  wixFontParam,
  IWixStyleFont,
  IStyleParam,
  IStyleParamValuesByType,
  createStylesParam,
} from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import { Alignment, alignmentName } from '../../utils/alignment';
import { buttonsStylesParams } from './buttonsStylesParams';
import { ISettingsParamsDefaults } from './defaultParams';
import { getRegularButtonProperties } from './Settings/Tabs/DesignTab/utils';
import { ButtonStyle } from './settingsParams';

export type StyleParam<T extends StyleParamType> = IStyleParam<T, IStyleParamValuesByType[T]>;
export type PackagePickerStylesParams = ReturnType<typeof createStylesParamsWithDefaults>;

export enum PlanCardPreset {
  Vertical = 0,
  Horizontal = 1,
}

export function createStylesParamsWithDefaults(defaults: ISettingsParamsDefaults = {}) {
  return {
    // TODO: Check how alignment works in Editor 3 and adjust accordingly
    textAlignment: createStylesParam('text-alignment', {
      type: StyleParamType.Number,
      getDefaultValue: () => Alignment.CENTER,
      metadata: {
        type: StyleType.textAlign,
        defaultValue: {
          type: 'static',
          value: 'center',
        },
      },
    }),
    titleAlignment: createStylesParam('title-alignment', {
      type: StyleParamType.Number,
      getDefaultValue: () => Alignment.CENTER,
      metadata: {
        type: StyleType.textAlign,
        defaultValue: {
          type: 'static',
          value: 'center',
        },
      },
    }),
    subtitleAlignment: createStylesParam('subtitle-alignment', {
      type: StyleParamType.Number,
      getDefaultValue: () => Alignment.CENTER,
      metadata: {
        type: StyleType.textAlign,
        defaultValue: {
          type: 'static',
          value: 'center',
        },
      },
    }),
    disclaimerAlignment: createStylesParam('disclaimer-alignment', {
      type: StyleParamType.Number,
      getDefaultValue: () => Alignment.CENTER,
      metadata: {
        type: StyleType.textAlign,
        ai: {
          description:
            'Text alignment for the optional legal/informational disclaimer text displayed at the bottom of the pricing plans section',
        },
        defaultValue: {
          type: 'static',
          value: 'center',
        },
      },
    }),
    descriptionAlignment: createStylesParam('description-alignment', {
      type: StyleParamType.Number,
      getDefaultValue: (params) => {
        const alignment = params.getStyleParamValue({ type: StyleParamType.Number, key: 'textAlignment' });
        return alignment ?? Alignment.CENTER;
      },
      metadata: {
        type: StyleType.textAlign,
        defaultValue: {
          type: 'static',
          value: 'center',
        },
      },
    }),
    benefitsAlignment: createStylesParam('benfits-alignment', {
      type: StyleParamType.Number,
      getDefaultValue: (params) => {
        const alignment = params.getStyleParamValue({ type: StyleParamType.Number, key: 'textAlignment' });
        return alignment ?? Alignment.CENTER;
      },
      metadata: {
        type: StyleType.textAlign,
        defaultValue: {
          type: 'static',
          value: 'center',
        },
      },
    }),
    planCardsSpacing: createStylesParam('plan-cards-spacing', {
      type: StyleParamType.Number,
      getDefaultValue: () => 32,
      metadata: {
        type: StyleType.gap,
        defaultValue: {
          type: 'static',
          value: 32,
        },
      },
    }),
    buttonStyle: createStylesParam('button-style', {
      type: StyleParamType.Number,
      getDefaultValue: () => ButtonStyle.FULL_CORNERED,
    }),

    backgroundColor: createStylesParam('app-bg-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1'),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      },
    }),
    titleBackgroundColor: createStylesParam('plan-top-bg-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 0),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      },
    }),
    titleBackgroundColorHighlighted: createStylesParam('primary-top-base-bg-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-8', 0.1),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      },
    }),
    planBenefitBackgroundColor: createStylesParam('plan-benefits-bg-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 0),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      },
    }),
    regularBenefitBulletColor: createStylesParam('regular-benefits-bullet-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-8'),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      },
    }),
    highlightedBenefitBulletColor: createStylesParam('highlighted-benefits-bullet-color', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'regular-benefits-bullet-color' });
        return color ?? wixColorParam('color-8')(params);
      },
      metadata: {
        type: StyleType.color,
        defaultValue: {
          type: 'ref',
          ref: 'regular-benefits-bullet-color',
          fallback: wixColor(CssColor.WstLinksAndActionsColor),
        },
      },
    }),
    planBenefitBackgroundColorHighlighted: createStylesParam('primary-benefits-base-bg-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 0),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      },
    }),
    cardBorderCornerRadius: createStylesParam('card-border-corner-radius', {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderRadius,
        defaultValue: {
          type: 'static',
          value: 0,
        },
      },
    }),
    cardBorderWidth: createStylesParam('card-border-width', {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: {
          type: 'static',
          value: 1,
        },
      },
    }),
    cardBorderColor: createStylesParam('card-border-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0.2),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: wixColor(CssColor.WstSystemLine1Color),
      },
    }),
    // TODO: support metadata for shadow
    regularCardShadowAngle: createStylesParam('regular-card-shadow-angle', {
      type: StyleParamType.Number,
      getDefaultValue: () => 315,
    }),
    regularCardShadowDistance: createStylesParam('regular-card-shadow-distance', {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
    }),
    regularCardShadowBlur: createStylesParam('regular-card-shadow-blur', {
      type: StyleParamType.Number,
      getDefaultValue: () => 25,
    }),
    regularCardShadowColor: createStylesParam('regular-card-shadow-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0.15),
    }),
    highlightedCardShadowAngle: createStylesParam('highlighted-card-shadow-angle', {
      type: StyleParamType.Number,
      getDefaultValue: ({ getStyleParamValue }) => {
        const angle = getStyleParamValue({ type: StyleParamType.Number, key: 'regular-card-shadow-angle' });
        return angle ?? 315;
      },
    }),
    highlightedCardShadowDistance: createStylesParam('highlighted-card-shadow-distance', {
      type: StyleParamType.Number,
      getDefaultValue: ({ getStyleParamValue }) => {
        const distance = getStyleParamValue({ type: StyleParamType.Number, key: 'regular-card-shadow-distance' });
        return distance ?? 0;
      },
    }),
    highlightedCardShadowBlur: createStylesParam('highlighted-card-shadow-blur', {
      type: StyleParamType.Number,
      getDefaultValue: ({ getStyleParamValue }) => {
        const blur = getStyleParamValue({ type: StyleParamType.Number, key: 'regular-card-shadow-blur' });
        return blur ?? 25;
      },
    }),
    highlightedCardShadowColor: createStylesParam('highlighted-card-shadow-color', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'regular-card-shadow-color' });
        return color ?? wixColorParam('color-5', 0.15)(params);
      },
    }),
    cardBorderColorHighlighted: createStylesParam('card-border-color-highlighted', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'card-border-color' });
        return color || wixColorParam('color-5', 0.2)(params);
      },
      metadata: {
        type: StyleType.borderColor,
        defaultValue: {
          type: 'ref',
          ref: 'card-border-color',
          fallback: wixColor(CssColor.WstSystemLine1Color),
        },
      },
    }),
    dividerWidth: createStylesParam('divider-width', {
      type: StyleParamType.Number,
      getDefaultValue: ({ getStyleParamValue }) =>
        getStyleParamValue({ type: StyleParamType.Boolean, key: 'show-benefits-dividers' }) ? 1 : 0,
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: {
          type: 'static',
          value: 1,
        },
      },
    }),
    dividerColor: createStylesParam('divider-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0.2),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: wixColor(CssColor.WstSystemLine1Color),
      },
    }),
    dividerColorHighlighted: createStylesParam('divider-color-highlighted', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0.2),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: {
          type: 'ref',
          ref: 'divider-color',
          fallback: wixColor(CssColor.WstSystemLine1Color),
        },
      },
    }),
    pageHeaderFont: createStylesParam('page-header-font', {
      type: StyleParamType.Font,
      getDefaultValue: wixFontParam('Page-title'),
      // metadata: {
      //   type: StyleType.font,
      //   defaultValue: {
      //     type: 'static',
      //     preset: 'Page-title',
      //   },
      // },
    }),
    pageHeaderMobileFontSize: createStylesParam('page-header-mobile-font-size', {
      type: StyleParamType.Number,
      getDefaultValue: () => 20,
      metadata: {
        type: StyleType.fontSize,
        defaultValue: {
          type: 'static',
          value: 20,
        },
      },
    }),
    pageHeaderColor: createStylesParam('page-header-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5'),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstHeading1Color),
      },
    }),
    pageSubtitleFont: createStylesParam('page-subtitle-font', {
      type: StyleParamType.Font,
      getDefaultValue: wixFontParam('Body-M', { size: 28 }),
      // metadata: {
      //   type: StyleType.font,
      //   defaultValue: {
      //     type: 'static',
      //     preset: 'Body-M',
      //     size: 28,
      //   },
      // },
    }),
    pageSubtitleMobileFontSize: createStylesParam('page-subtitle-mobile-font-size', {
      type: StyleParamType.Number,
      getDefaultValue: () => 14,
      metadata: {
        type: StyleType.fontSize,
        defaultValue: {
          type: 'static',
          value: 14,
        },
      },
    }),
    pageSubtitleColor: createStylesParam('page-subtitle-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5'),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstHeading2Color),
      },
    }),
    pageDisclaimerFont: createStylesParam('page-disclaimer-font', {
      type: StyleParamType.Font,
      getDefaultValue: wixFontParam('Body-M', { size: 14 }),
      // metadata: {
      //   type: StyleType.font,
      //   defaultValue: {
      //     type: 'static',
      //     preset: 'Body-M',
      //     size: 14,
      //   },
      // },
    }),
    pageDisclaimerMobileFontSize: createStylesParam('page-disclaimer-mobile-font-size', {
      type: StyleParamType.Number,
      getDefaultValue: () => 14,
      metadata: {
        type: StyleType.fontSize,
        defaultValue: {
          type: 'static',
          value: 14,
        },
      },
    }),
    pageDisclaimerColor: createStylesParam('page-disclaimer-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5'),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstParagraph1Color),
      },
    }),
    benefitsSpacing: createStylesParam('benefits-spacing', {
      type: StyleParamType.Number,
      getDefaultValue: () => 1.5,
      metadata: {
        type: StyleType.gap,
        defaultValue: {
          type: 'static',
          value: 1.5,
        },
      },
    }),
    horizontalPadding: createStylesParam('horizontal-padding', {
      type: StyleParamType.Number,
      getDefaultValue: () => 30,
      metadata: {
        type: StyleType.padding,
        defaultValue: {
          type: 'static',
          value: 30,
        },
      },
    }),
    verticalPadding: createStylesParam('vertical-padding', {
      type: StyleParamType.Number,
      getDefaultValue: () => 30,
      metadata: {
        type: StyleType.padding,
        defaultValue: {
          type: 'static',
          value: 30,
        },
      },
    }),
    planTitleFont: createStylesParam('plan-title-font', {
      type: StyleParamType.Font,
      getDefaultValue: wixFontParam('Body-M', {
        size: 20,
      }),
      // metadata: {
      //   type: StyleType.font,
      //   defaultValue: {
      //     type: 'static',
      //     preset: 'Body-M',
      //     size: 20,
      //   },
      // },
    }),
    planTitleMobileFontSize: createStylesParam('plan-title-mobile-font-size', {
      type: StyleParamType.Number,
      getDefaultValue: () => 20,
      metadata: {
        type: StyleType.fontSize,
        defaultValue: {
          type: 'static',
          value: 20,
        },
      },
    }),
    planTitleColor: createStylesParam('plan-title-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5'),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstHeading1Color),
      },
    }),
    planPriceFont: createStylesParam('plan-price-font', {
      type: StyleParamType.Font,
      getDefaultValue: ({ textPresets }) => {
        const { value: _, ...font } = wixFontParam('Body-M', { size: 70 })({ textPresets });
        return { ...font } as IWixStyleFont;
      },
      // metadata: {
      //   type: StyleType.font,
      //   defaultValue: {
      //     type: 'static',
      //     preset: 'Body-M',
      //     size: 70,
      //   },
      // },
    }),
    planPriceMobileFontSize: createStylesParam('plan-price-mobile-font-size', {
      type: StyleParamType.Number,
      getDefaultValue: () => 70,
      metadata: {
        type: StyleType.fontSize,
        defaultValue: {
          type: 'static',
          value: 70,
        },
      },
    }),
    planPriceColor: createStylesParam('plan-price-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5'),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstParagraph1Color),
      },
    }),
    planTaglineFont: createStylesParam('plan-tagline-font', {
      type: StyleParamType.Font,
      getDefaultValue: ({ textPresets }) => {
        const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
        return { ...font } as IWixStyleFont;
      },
      // metadata: {
      //   type: StyleType.font,
      //   ai: {
      //     description: 'Font style for the short promotional phrase/tagline shown below the plan title on each pricing card',
      //   },
      //   defaultValue: {
      //     type: 'static',
      //     preset: 'Body-M',
      //     size: 14,
      //   },
      // },
    }),
    planTaglineColor: createStylesParam('plan-tagline-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5'),
      metadata: {
        type: StyleType.color,
        ai: {
          description:
            'Text color for the short promotional phrase/tagline shown below the plan title on regular pricing cards',
        },
        defaultValue: wixColor(CssColor.WstParagraph1Color),
      },
    }),
    planSetupFeeFont: createStylesParam('plan-setup-fee-font', {
      type: StyleParamType.Font,
      getDefaultValue: ({ textPresets }) => {
        const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
        return { ...font } as IWixStyleFont;
      },
      // metadata: {
      //   type: StyleType.font,
      //   ai: {
      //     description: 'Font style for the one-time setup fee amount displayed on pricing cards (charged once at subscription start)',
      //   },
      //   defaultValue: {
      //     type: 'static',
      //     preset: 'Body-M',
      //     size: 14,
      //   },
      // },
    }),
    planSetupFeeColor: createStylesParam('plan-setup-fee-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5'),
      metadata: {
        type: StyleType.color,
        ai: {
          description:
            'Text color for the one-time setup fee amount on regular pricing cards (charged once at subscription start)',
        },
        defaultValue: wixColor(CssColor.WstParagraph1Color),
      },
    }),
    planBenefitsFont: createStylesParam('plan-benefits-font', {
      type: StyleParamType.Font,
      getDefaultValue: ({ textPresets }) => {
        const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
        return { ...font } as IWixStyleFont;
      },
      // metadata: {
      //   type: StyleType.font,
      //   defaultValue: {
      //     type: 'static',
      //     preset: 'Body-M',
      //     size: 14,
      //   },
      // },
    }),
    planBenefitsMobileFontSize: createStylesParam('plan-benefits-mobile-font-size', {
      type: StyleParamType.Number,
      getDefaultValue: () => 14,
      metadata: {
        type: StyleType.fontSize,
        defaultValue: {
          type: 'static',
          value: 14,
        },
      },
    }),
    planBenefitsColor: createStylesParam('plan-benefits-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5'),
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor(CssColor.WstParagraph1Color),
      },
    }),
    planRecurringOptionsFont: createStylesParam('plan-recurring-options-font', {
      type: StyleParamType.Font,
      getDefaultValue: (params) => {
        const priceFont = params.getStyleParamValue({ type: StyleParamType.Font, key: 'plan-price-font' });
        const { value: _, ...font } = wixFontParam('Body-M', { size: 12 })(params);
        return priceFont || ({ ...font } as IWixStyleFont);
      },
      // metadata: {
      //   type: StyleType.font,
      //   ai: {
      //     description: 'Font style for the billing frequency text displayed with the price (e.g., "/month", "/year", "per week")',
      //   },
      //   defaultValue: {
      //     type: 'ref',
      //     ref: 'plan-price-font',
      //     fallback: {
      //       type: 'static',
      //       preset: 'Body-M',
      //       size: 12,
      //     },
      //   },
      // },
    }),
    planRecurringOptionsColor: createStylesParam('plan-recurring-options-color', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'plan-price-color' });
        return color || wixColorParam('color-5')(params);
      },
      metadata: {
        type: StyleType.color,
        ai: {
          description: 'Text color for the billing frequency text on regular pricing cards (e.g., "/month", "/year")',
        },
        defaultValue: {
          type: 'ref',
          ref: 'plan-price-color',
          fallback: wixColor(CssColor.WstParagraph1Color),
        },
      },
    }),
    planDurationFont: createStylesParam('plan-duration-font', {
      type: StyleParamType.Font,
      getDefaultValue: (params) => {
        const taglineFont = params.getStyleParamValue({ type: StyleParamType.Font, key: 'plan-tagline-font' });
        const { value: _, ...font } = wixFontParam('Body-M', { size: 12 })(params);
        return taglineFont || ({ ...font } as IWixStyleFont);
      },
      // metadata: {
      //   type: StyleType.font,
      //   ai: {
      //     description: 'Font style for the subscription commitment period text (e.g., "12 months")',
      //   },
      //   defaultValue: {
      //     type: 'ref',
      //     ref: 'plan-tagline-font',
      //     fallback: {
      //       type: 'static',
      //       preset: 'Body-M',
      //       size: 12,
      //     },
      //   },
      // },
    }),
    planDurationColor: createStylesParam('plan-duration-color', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'plan-tagline-color' });
        return color || wixColorParam('color-5')(params);
      },
      metadata: {
        type: StyleType.color,
        ai: {
          description:
            'Text color for the subscription commitment period on regular pricing cards (e.g., "12 months", "1 week")',
        },
        defaultValue: {
          type: 'ref',
          ref: 'plan-tagline-color',
          fallback: wixColor(CssColor.WstParagraph1Color),
        },
      },
    }),
    showPageTitle: createStylesParam('show-page-title', {
      type: StyleParamType.Boolean,
      getDefaultValue: () => (defaults.showPageTitle !== undefined ? defaults.showPageTitle : true),
      metadata: {
        type: StyleType.booleanValue,
        defaultValue: {
          type: 'static',
          value: true,
        },
      },
    }),
    showPageSubtitle: createStylesParam('show-page-subtitle', {
      type: StyleParamType.Boolean,
      getDefaultValue: () => false,
      metadata: {
        type: StyleType.booleanValue,
        defaultValue: {
          type: 'static',
          value: false,
        },
      },
    }),
    showPlanName: createStylesParam('show-plan-name', {
      type: StyleParamType.Boolean,
      getDefaultValue: () => true,
      metadata: {
        type: StyleType.booleanValue,
        defaultValue: {
          type: 'static',
          value: true,
        },
      },
    }),
    showTagline: createStylesParam('show-plan-tagline', {
      type: StyleParamType.Boolean,
      getDefaultValue: () => true,
      metadata: {
        type: StyleType.booleanValue,
        defaultValue: {
          type: 'static',
          value: true,
        },
      },
    }),
    showBenefits: createStylesParam('show-plan-benefits', {
      type: StyleParamType.Boolean,
      getDefaultValue: () => true,
      metadata: {
        type: StyleType.booleanValue,
        defaultValue: {
          type: 'static',
          value: true,
        },
      },
    }),
    showBenefitsDividers: createStylesParam('show-benefits-dividers', {
      type: StyleParamType.Boolean,
      getDefaultValue: () => true,
      metadata: {
        type: StyleType.booleanValue,
        defaultValue: {
          type: 'static',
          value: true,
        },
      },
    }),
    showDisclaimer: createStylesParam('show-disclaimer', {
      type: StyleParamType.Boolean,
      getDefaultValue: () => false,
      metadata: {
        type: StyleType.booleanValue,
        defaultValue: {
          type: 'static',
          value: false,
        },
      },
    }),
    showImage: createStylesParam('show-image', {
      type: StyleParamType.Boolean,
      getDefaultValue: () => false,
      metadata: {
        type: StyleType.booleanValue,
        defaultValue: {
          type: 'static',
          value: false,
        },
      },
    }),
    // TODO: Figure out where this will be
    showRibbon: createStylesParam('show-ribbon', {
      type: StyleParamType.Boolean,
      getDefaultValue: () => true,
    }),
    ribbonBackgroundColor: createStylesParam('ribbon-bg-opacity-and-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-8'),
      metadata: {
        type: StyleType.color,
        ai: {
          description: 'Background color of the featured badge/ribbon displayed on highlighted pricing plans',
        },
        defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
      },
    }),
    ribbonFont: createStylesParam('ribbon-font', {
      type: StyleParamType.Font,
      getDefaultValue: ({ textPresets }) => {
        const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
        return { ...font } as IWixStyleFont;
      },
      // metadata: {
      //   type: StyleType.font,
      //   ai: {
      //     description: 'Font style for the text inside the featured badge/ribbon (e.g., "Most Popular", "Best Value")',
      //   },
      //   defaultValue: {
      //     type: 'static',
      //     preset: 'Body-M',
      //     size: 14,
      //   },
      // },
    }),
    ribbonColor: createStylesParam('ribbon-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1'),
      metadata: {
        type: StyleType.color,
        ai: {
          description:
            'Text color for the label inside the featured badge/ribbon displayed on highlighted pricing plans',
        },
        defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
      },
    }),
    ribbonBorderWidth: createStylesParam('ribbon-border-width', {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderWidth,
        ai: {
          description: 'Border thickness of the featured badge/ribbon shown on highlighted pricing plans',
        },
        defaultValue: {
          type: 'static',
          value: 0,
        },
      },
    }),
    ribbonBorderColor: createStylesParam('ribbon-border-color', {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0),
      metadata: {
        type: StyleType.borderColor,
        ai: {
          description: 'Border color of the featured badge/ribbon displayed on highlighted pricing plans',
        },
        defaultValue: {
          type: 'static',
          preset: CssColor.WstLinksAndActionsColor,
          opacity: 0,
        },
      },
    }),
    planTitleColorHighlighted: createStylesParam('plan-title-color-highlighted', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'plan-title-color' });
        return color || wixColorParam('color-5')(params);
      },
      metadata: {
        type: StyleType.color,
        defaultValue: {
          type: 'ref',
          ref: 'plan-title-color',
          fallback: wixColor(CssColor.WstHeading1Color),
        },
      },
    }),
    planPriceColorHighlighted: createStylesParam('plan-price-color-highlighted', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'plan-price-color' });
        return color || wixColorParam('color-5')(params);
      },
      metadata: {
        type: StyleType.color,
        defaultValue: {
          type: 'ref',
          ref: 'plan-price-color',
          fallback: wixColor(CssColor.WstParagraph1Color),
        },
      },
    }),
    planRecurringOptionsColorHighlighted: createStylesParam('plan-recurring-options-color-highlighted', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'plan-price-color' });
        return color || wixColorParam('color-5')(params);
      },
      metadata: {
        type: StyleType.color,
        ai: {
          description: 'Text color for the billing frequency text on the featured/highlighted pricing card',
        },
        defaultValue: {
          type: 'ref',
          ref: 'plan-price-color',
          fallback: wixColor(CssColor.WstParagraph1Color),
        },
      },
    }),
    planTaglineColorHighlighted: createStylesParam('plan-tagline-color-highlighted', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'plan-tagline-color' });
        return color || wixColorParam('color-5')(params);
      },
      metadata: {
        type: StyleType.color,
        ai: {
          description:
            'Text color for the short promotional phrase/tagline shown below the plan title on the featured/highlighted pricing card',
        },
        defaultValue: {
          type: 'ref',
          ref: 'plan-tagline-color',
          fallback: wixColor(CssColor.WstParagraph1Color),
        },
      },
    }),
    planSetupFeeColorHighlighted: createStylesParam('plan-setup-fee-color-highlighted', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'plan-setup-fee-color' });
        return color || wixColorParam('color-5')(params);
      },
      metadata: {
        type: StyleType.color,
        ai: {
          description: 'Text color for the one-time setup fee amount on the featured/highlighted pricing card',
        },
        defaultValue: {
          type: 'ref',
          ref: 'plan-setup-fee-color',
          fallback: wixColor(CssColor.WstParagraph1Color),
        },
      },
    }),
    planDurationColorHighlighted: createStylesParam('plan-duration-color-highlighted', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'plan-duration-color' });
        return color || wixColorParam('color-3')(params);
      },
      metadata: {
        type: StyleType.color,
        ai: {
          description: 'Text color for the subscription commitment period on the featured/highlighted pricing card',
        },
        defaultValue: {
          type: 'ref',
          ref: 'plan-duration-color',
          fallback: wixColor(CssColor.WstParagraph1Color),
        },
      },
    }),
    planBenefitsColorHighlighted: createStylesParam('plan-benefits-color-highlighted', {
      type: StyleParamType.Color,
      getDefaultValue: (params) => {
        const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'plan-benefits-color' });
        return color || wixColorParam('color-5')(params);
      },
      metadata: {
        type: StyleType.color,
        defaultValue: {
          type: 'ref',
          ref: 'plan-benefits-color',
          fallback: wixColor(CssColor.WstParagraph1Color),
        },
      },
    }),
    cardBorderWidthHighlighted: createStylesParam('card-border-width-highlighted', {
      type: StyleParamType.Number,
      getDefaultValue: (params) => {
        const width = params.getStyleParamValue({ type: StyleParamType.Number, key: 'card-border-width' });
        return width ?? 1;
      },
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: {
          type: 'ref',
          ref: 'card-border-width',
          fallback: {
            type: 'static',
            value: 1,
          },
        },
      },
    }),

    planCoverImageHeight: createStylesParam('plan-cover-image-height', {
      type: StyleParamType.Number,
      getDefaultValue: () => 150,
      metadata: {
        type: StyleType.height,
        defaultValue: {
          type: 'static',
          value: 150,
        },
      },
    }),

    planCardPreset: createStylesParam('plan-card-preset', {
      type: StyleParamType.Number,
      getDefaultValue: () => PlanCardPreset.Vertical,
    }),

    ...buttonsStylesParams,
  };
}

const componentStyles = createStylesParamsWithDefaults();

export default componentStyles;

const getVisibilityCssVariables: CustomCssVarsFn = ({ styleParams }) => {
  const booleans = styleParams.booleans;
  return Object.keys(booleans).reduce((result, key) => {
    result[key] = booleans[key] ? 'visible' : 'hidden';
    return result;
  }, {} as Record<string, string>);
};

const getAlignmentCssVariables: CustomCssVarsFn = ({ styleParams }) => {
  const buttonParams = getRegularButtonProperties(styleParams.numbers[componentStyles.buttonStyle.key!]);
  const buttonAlignmentValue = styleParams.numbers[buttonParams.alignment.key!];

  return {
    [componentStyles.titleAlignment.key!]: alignmentName(styleParams.numbers[componentStyles.titleAlignment.key!]),
    [componentStyles.subtitleAlignment.key!]: alignmentName(
      styleParams.numbers[componentStyles.subtitleAlignment.key!],
    ),
    [componentStyles.disclaimerAlignment.key!]: alignmentName(
      styleParams.numbers[componentStyles.disclaimerAlignment.key!],
    ),
    [componentStyles.descriptionAlignment.key!]: alignmentName(
      styleParams.numbers[componentStyles.descriptionAlignment.key!],
    ),
    [componentStyles.benefitsAlignment.key!]: alignmentName(
      styleParams.numbers[componentStyles.benefitsAlignment.key!],
    ),
    'button-alignment': alignmentName(buttonAlignmentValue),
  };
};

const getShadowOffsetCssVariables: CustomCssVarsFn = ({ styleParams }) => {
  const cardShadowAngle = styleParams.numbers[componentStyles.regularCardShadowAngle.key!];
  const cardShadowDistance = styleParams.numbers[componentStyles.regularCardShadowDistance.key!];

  const highlightedCardShadowAngle = styleParams.numbers[componentStyles.highlightedCardShadowAngle.key!];
  const highlightedCardShadowDistance = styleParams.numbers[componentStyles.highlightedCardShadowDistance.key!];

  return {
    'regular-card-shadow-offset-x': -Math.round(Math.sin((cardShadowAngle * Math.PI) / 180) * cardShadowDistance),
    'regular-card-shadow-offset-y': Math.round(Math.cos((cardShadowAngle * Math.PI) / 180) * cardShadowDistance),

    'highlighted-card-shadow-offset-x': -Math.round(
      Math.sin((highlightedCardShadowAngle * Math.PI) / 180) * highlightedCardShadowDistance,
    ),
    'highlighted-card-shadow-offset-y': Math.round(
      Math.cos((highlightedCardShadowAngle * Math.PI) / 180) * highlightedCardShadowDistance,
    ),
  };
};

export const customCssVars: CustomCssVarsFn = (params) => {
  const visibilityValues = getVisibilityCssVariables(params);
  const alignmentValues = getAlignmentCssVariables(params);
  const shadowOffsetValues = getShadowOffsetCssVariables(params);

  return {
    ...visibilityValues,
    ...alignmentValues,
    ...shadowOffsetValues,
  };
};
